<script>
	import {
		AmbientLight,
		Color,
		CylinderGeometry,
		DirectionalLight,
		Group,
		Mesh,
		MeshStandardMaterial,
		PerspectiveCamera,
		Scene,
		Vector3,
		WebGLRenderer
	} from "three";
	import { ConvexGeometry } from "three/examples/jsm/geometries/ConvexGeometry.js";
	import { onDestroy, onMount } from "svelte";
	import profile, { profiles } from "../../../stores/profile.js";

	export let background = "hsl(0, 0%, 85%)";
	export let heading = 0.0;
	export let speed = 0.0;

	const BUMP_FREQUENCY = 8;
	const BUMP_AMPLITUDE = 0.012;
	const FRAME_STEP = 0.016;

	// three no longer scales light intensity by PI (physicallyCorrectLights was removed), so keep the same exposure here
	const LIGHT_SCALE = Math.PI;

	let container;
	let canvas;
	let wheels = [];

	const scene = new Scene();
	const camera = new PerspectiveCamera(45, 1, 0.1, 2000);
	const chassis = new Group();
	const body = new Mesh(new ConvexGeometry(), new MeshStandardMaterial({ color: new Color("orangered") }));
	const wheelGeometry = new CylinderGeometry(1, 1, 0.2, 16);
	const wheelMaterial = new MeshStandardMaterial({ color: new Color("black") });

	camera.position.set(1, 2, 3);
	camera.lookAt(0, 0, 0);

	const ambient = new AmbientLight(0xffffff, 0.6 * LIGHT_SCALE);
	const sun = new DirectionalLight(0xffffff, 0.6 * LIGHT_SCALE);
	sun.position.set(-2, 3, 2);

	scene.add(chassis, ambient, sun);
	chassis.add(body);

	$: currentProfile = $profile?.vehicle ? $profile : profiles.moto;
	$: scene.background = new Color(background);
	$: chassis.rotation.y = heading;
	$: updateBody(currentProfile);
	$: updateWheels(currentProfile.vehicle.wheels);

	function verticesFromModel(model) {
		if (!Array.isArray(model)) return [];

		return model.filter(Array.isArray).map((point) => new Vector3(...point));
	}

	function updateBody(selected) {
		const geometry = new ConvexGeometry(verticesFromModel(selected.vehicle.model));

		body.geometry.dispose();
		body.geometry = geometry;
		body.material.color.set(selected.color || "orangered");
	}

	function updateWheels(models) {
		wheels.forEach((wheel) => chassis.remove(wheel));

		wheels = models.map((model) => {
			const wheel = new Mesh(wheelGeometry, wheelMaterial);

			wheel.scale.set(model.radius, 1, model.radius);
			wheel.position.set(model.position[0], model.position[1] + 0.3, model.position[2]);
			wheel.rotation.set(Math.PI / 2, 0, 0);

			chassis.add(wheel);

			return wheel;
		});
	}

	function resize(renderer) {
		const ratio = typeof devicePixelRatio === "undefined" ? 1 : devicePixelRatio;
		const width = container.clientWidth / ratio;
		const height = container.clientHeight / ratio;

		renderer.setPixelRatio(ratio);
		renderer.setSize(width, height, false);

		camera.aspect = width / height;
		camera.updateProjectionMatrix();
	}

	onMount(() => {
		const renderer = new WebGLRenderer({ canvas });
		const observer = new ResizeObserver(() => resize(renderer));

		resize(renderer);
		observer.observe(container);

		let time = 0;
		let frame = requestAnimationFrame(function loop() {
			time += FRAME_STEP;

			const bump = speed > 0 ? Math.sin(time * BUMP_FREQUENCY) * BUMP_AMPLITUDE : 0;

			body.position.set(0, 0.3 + bump, 0);
			renderer.render(scene, camera);

			frame = requestAnimationFrame(loop);
		});

		return () => {
			cancelAnimationFrame(frame);
			observer.disconnect();
			renderer.forceContextLoss();
			renderer.dispose();
		};
	});

	onDestroy(() => {
		body.geometry.dispose();
		body.material.dispose();
		wheelGeometry.dispose();
		wheelMaterial.dispose();
	});
</script>

<div class="scene" bind:this={container}>
	<canvas bind:this={canvas}></canvas>
</div>

<style>
	.scene,
	canvas {
		position: absolute;
		width: 100%;
		height: 100%;
		left: 0;
		top: 0;
	}
</style>
